import { useContext, useState } from "react";
import { Box, CircularProgress, Divider, Drawer, LinearProgress, Tooltip, Typography } from "@mui/material";
import { styled, Link } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { formatDate } from "utils/date-utils";
import { ProjectContext } from "./project-layout";
import { AuthContext, DrawerHeader, Ico_Warning, SideDrawer } from '@sagecentral/core-ui';
import { GridColDef, GridPaginationModel, GridSortItem, GridSortModel } from '@mui/x-data-grid-pro';
import { useProjectProblemLog } from "services/projects/project-problem-log-hook";
import { GridMetaData } from "models/grids";
import { CentralDataGridPro, Ico_Close } from "@sagecentral/core-ui";
import { useNavigate } from "react-router-dom";
import { GridBox } from "components/shared/grids";
import { useFeatureEnabled } from "hooks/shared";
import { Link as RouterLink } from 'react-router-dom';

const KeyGrid = styled(Grid)({
    fontWeight: "bold"
});

const ProjectsOverview = () => {
    const defaultPage = 0;
    const defaultPageSize = 10;
    const defaultSortField = 'date';
    const defaultSortDir = 'asc';
    const defaultSort: GridSortItem[] = [{
        field: 'resolved',
        sort: 'asc'
    }, {
        field: 'date',
        sort: 'asc'
    }];

    const auth = useContext(AuthContext);
    const project = useContext(ProjectContext);
    const [drawer, setDrawer] = useState(false);
    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: defaultPage, pageSize: defaultPageSize });
    const [sortModel, setSortModel] = useState<GridSortModel>();
    const [gridMetaData, setGridMetaData] = useState<GridMetaData>({
        page: pageModel.page,
        pageSize: pageModel.pageSize,
        sort: defaultSort.map(s => {
            return { field: s.field, dir: s.sort };
        })
    });
    const [ wave4Enabled ] = useFeatureEnabled('wave-4');

    const navigate = useNavigate();
    const navigateToTask = (taskId: number) => {
        navigate(`/tasks/${taskId}/overview`);
      };

    let [projectProblemLog, projectProblemLogLoading, projectProblemLogerror] = useProjectProblemLog({ projectId: project?.id || 0, gridMetaData: gridMetaData });

    const onProblemLogClick = (event?: any) => {
        if (event) {
            event.preventDefault();
        }
        setDrawer(!drawer);
    };

    const handlePaginationModelChange = async (model: GridPaginationModel) => {
        setPageModel({
            page: model.page,
            pageSize: model.pageSize
        });
        gridMetaData.page = model.page;
        gridMetaData.pageSize = model.pageSize;
        setGridMetaData(gridMetaData);
    }

    const handleSortModelChange = (model: GridSortModel) => {
        console.log(JSON.stringify(model));
        const sortColumn = model[0]?.field || defaultSortField;
        const sortDirection = model[0]?.sort || defaultSortDir;
        setSortModel([{
            field: sortColumn,
            sort: sortDirection
        }]);
        setPageModel({
            page: defaultPage,
            pageSize: defaultPageSize
        });
        gridMetaData.sort = [{ field: sortColumn, dir: sortDirection }]
        setGridMetaData(gridMetaData);
    };

    const columns: GridColDef[] = [
        {
            field: 'problem',
            headerName: 'Problem',
            headerClassName: 'header',
            sortable: true,
            hideSortIcons: false,
            resizable: true,
            flex: 1,
            width: 300,
            renderHeader: (params) => {
                return (
                    <Tooltip title="Problem" arrow>
                        <div>{"Problem"}</div>
                    </Tooltip>
                )
            }
        },
        {
            field: 'date',
            headerName: 'Date',
            headerClassName: 'header',
            sortable: true,
            hideSortIcons: false,
            resizable: true,
            minWidth: 140,
            renderCell: (params) => {
                return (<div>{formatDate(params.value)}</div>)
            }
        },
        {
            field: 'resolved',
            headerName: 'Resolved',
            headerClassName: 'header',
            sortable: true,
            hideSortIcons: false,
            resizable: true,
            minWidth: 50,
            renderCell: (params) => {
                const label = params.value ? 'Yes' : 'No';
                return (
                    params.value ? <Typography>{label}</Typography> : <Typography color="#FF0000">{label}</Typography>
                )
            }
        },
        {
            field: 'task',
            headerName: 'Task',
            headerClassName: 'header',
            sortable: true,
            hideSortIcons: false,
            resizable: true,
            minWidth: 250,
            renderCell: (params) => {
                const name = params.row.task;
                const taskId = params.row.taskId;
                const hasAccess = params.row.userHasAccess;
                return (
                    <div style={{ whiteSpace: 'pre-wrap', flexWrap: 'nowrap' }}>
                        {hasAccess && 
                        <Link to={`/tasks/${taskId}/overview`} component={RouterLink} >
                            {name}
                        </Link>
                        || name}
                    </div>
                )
            }
        },
        {
            field: 'workItem',
            headerName: 'Work Item',
            headerClassName: 'header',
            sortable: true,
            hideSortIcons: false,
            resizable: true,
            width: 200,
            renderCell: (params) => {
                const workItem = params.row.workItem;
                const workItemId = params.row.workItemId;
                const href = wave4Enabled 
                    ? process.env.REACT_APP_PUBLIC_URL + `/workitems/${workItemId}/overview`
                    : process.env.REACT_APP_API_URL + `/WorkitemEnduser/EditWorkitem?workitemID=${workItemId}`;;
                if (workItemId > 0) return (
                    <Link  to={href} component={RouterLink}>{workItem}</Link>
                )
                else return (
                    <div>{workItem}</div>
                )
            }
        },
        {
            field: 'workflow',
            headerName: 'Workflow',
            headerClassName: 'header',
            sortable: true,
            hideSortIcons: false,
            resizable: true,
            width: 120,
            renderCell: (params) => {
                const workflow = params.row.workflow;
                const workflowId = params.row.workflowId;
                const href = process.env.REACT_APP_CLASSIC_URL + `/WorkflowInstance/EditWorkflow?workflowInstanceID=${workflowId}`;

                if (workflowId > 0) return (
                    <Link to={href} component={RouterLink}>{workflow}</Link>
                )
                else return (
                    <div>{workflow}</div>
                )
            }
        }
    ] as GridColDef[];

    const getData = () => (projectProblemLogLoading ? [] : projectProblemLog?.projectTasks || []);
    const isFilterApplied = () => ((gridMetaData.filterParam?.filters.length || 0) > 0);
    return (
        <>
            <Box>
                <Grid container spacing={1} sx={{ marginBottom: '10px' }}>
                    <KeyGrid xs={1}>Project Title:</KeyGrid>
                    <Grid>{project?.name}</Grid>
                </Grid>
                <Grid container spacing={1} sx={{ marginBottom: '10px' }}>
                    <KeyGrid xs={1}>Status:</KeyGrid>
                    <Grid>{project?.projectProgress}</Grid>
                </Grid>
                <Grid container spacing={1} sx={{ marginBottom: '10px' }}>
                    <KeyGrid xs={1}>Start Date:</KeyGrid>
                    <Grid>{formatDate(project?.startDate ?? '')}</Grid>
                </Grid>
                <Grid container spacing={1} sx={{ marginBottom: '10px' }}>
                    <KeyGrid xs={1}>End Date:</KeyGrid>
                    <Grid>{formatDate(project?.endDate ?? '')}</Grid>
                </Grid>
                <Grid container spacing={1} sx={{ marginBottom: '10px' }}>
                    <KeyGrid xs={1}>Workspace:</KeyGrid>
                    <Grid>{project?.workspaceInstanceName}</Grid>
                </Grid>
            </Box>

            {projectProblemLog && projectProblemLog.hasViewPermission &&
                <Box sx={{ mt: '24px', ml: '0px', mb: '32px', display: 'flex' }}>
                    {projectProblemLogLoading && <>
                        <Box sx={{ width: '180px' }}>
                            loading problem log ... <LinearProgress sx={{ height: '2px', marginTop: '3px' }} />
                        </Box>
                    </>}
                    {projectProblemLogLoading == false && projectProblemLog && projectProblemLog.projectTasks && projectProblemLog.projectTasks.length > 0 && <>
                        <a rel="noopener noreferrer" href='' onClick={onProblemLogClick}
                            style={{ color: '#0066CC', cursor: 'pointer' }}>
                            View Project Problem Log
                        </a>
                        {projectProblemLog.projectTasks.some(f => f.resolved == false) &&
                            <Box sx={{ ml: '4px' }}>
                                <Ico_Warning color='error' />
                            </Box>
                        }
                    </>
                    }
                    {projectProblemLogLoading == false && projectProblemLog && projectProblemLog.projectTasks && projectProblemLog.projectTasks.length == 0 && <>
                        No Project Problem Log Found.
                    </>
                    }
                </Box>
            }

            <Divider />

            <SideDrawer
                open={drawer}
                onClose={onProblemLogClick}
                header={
                    <DrawerHeader
                        title='Project Problem Log'
                        onClose={() => onProblemLogClick()} />
                }>
                <>
                    {projectProblemLog && projectProblemLog.projectTasks && projectProblemLog.projectTasks.length > 0 &&
                        <GridBox getData={getData} minRowHeight={52}>
                            <CentralDataGridPro
                                rows={getData()}
                                rowCount={projectProblemLog.totalItems}
                                loading={projectProblemLogLoading}
                                columns={columns}
                                sortModel={sortModel}
                                filterMode="client"
                                sortingMode="client"
                                pagination={true}
                                paginationModel={pageModel}
                                onSortModelChange={handleSortModelChange}
                                onPaginationModelChange={handlePaginationModelChange}
                                onFilterModelChange={isFilterApplied}
                                paginationMode='server'
                                slots={{
                                    loadingOverlay: () => (<CircularProgress/>),
                                }}
                            />
                        </GridBox>
                    }
                </>
            </SideDrawer>
        </>
    );
}

export default ProjectsOverview